// Fase 7A — Richiami (vista operativa a lista). CROSS-TENANT.
// Sezioni: DA FARE OGGI · SCADUTI · PROSSIMI · COMPLETATI (storico recente).
import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, PhoneCall, Store, Clock } from "lucide-react";
import { api, createAgencyClient } from "@/lib/api";
import { useAgencyWorkspace } from "@/context/AgencyWorkspaceContext";
import { callbackTone, fmtDateTime, fmtTime, isOverdue, isToday, CB_LEGEND } from "@/lib/callbacks";
import CallbackDetail from "@/pages/admin/agency/CallbackDetail";
import CallbackModal from "@/components/CallbackModal";
import AgencyContactDrawerHost from "@/pages/admin/agency/AgencyContactDrawerHost";

function Row({ cb, onClick }) {
  const tone = callbackTone(cb);
  return (
    <button data-testid={`richiami-row-${cb.id}`} onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)] text-left">
      <div className="text-center w-14 flex-shrink-0">
        <div className="font-black text-sm">{fmtTime(cb.scheduled_at)}</div>
        <div className="text-[9px] text-[color:var(--sp-text-muted)]">{new Date(cb.scheduled_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}</div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-sm truncate">{cb.client?.name || "Contatto"}</div>
        <div className="text-xs text-[color:var(--sp-text-muted)] flex items-center gap-1 truncate"><Store className="h-3 w-3" /> {cb.tenant?.name}</div>
      </div>
      <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold flex-shrink-0 ${tone.cls}`}>{tone.label}</span>
    </button>
  );
}

function Section({ title, items, onOpen, tid, accent }) {
  return (
    <div className="sp-card p-4" data-testid={tid}>
      <div className="flex items-center gap-2 mb-3">
        <h2 className={`font-display text-sm font-black uppercase tracking-wider ${accent || ""}`}>{title}</h2>
        <span className="text-xs font-bold text-[color:var(--sp-text-muted)]">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-[color:var(--sp-text-muted)] italic py-2">Nessun richiamo.</div>
      ) : (
        <div className="space-y-2">{items.map((cb) => <Row key={cb.id} cb={cb} onClick={() => onOpen(cb)} />)}</div>
      )}
    </div>
  );
}

export default function AgencyCallbacksPage() {
  const { availableTenants } = useAgencyWorkspace();
  const [rows, setRows] = useState([]);
  const [salon, setSalon] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [contact, setContact] = useState(null);
  const [followUp, setFollowUp] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (salon !== "all") params.set("tenant_id", salon);
      const r = await api.get(`/agency/callbacks${params.toString() ? `?${params}` : ""}`);
      setRows(r.data || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [salon]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const groups = useMemo(() => {
    const now = new Date();
    const today = [], overdue = [], upcoming = [], completed = [];
    rows.forEach((cb) => {
      if (cb.status === "completed") { completed.push(cb); return; }
      if (cb.status === "cancelled") return;
      if (isOverdue(cb, now)) { overdue.push(cb); return; }
      if (isToday(cb.scheduled_at, now)) { today.push(cb); return; }
      if (new Date(cb.scheduled_at) > now) upcoming.push(cb);
      else today.push(cb); // passato oggi ma entro grazia
    });
    const byTime = (a, b) => a.scheduled_at.localeCompare(b.scheduled_at);
    today.sort(byTime); overdue.sort(byTime); upcoming.sort(byTime);
    completed.sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""));
    return { today, overdue, upcoming, completed: completed.slice(0, 20) };
  }, [rows]);

  return (
    <div data-testid="aw-callbacks-page">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h1 className="font-display text-2xl font-black flex items-center gap-2 mr-2"><PhoneCall className="h-6 w-6 text-[color:var(--sp-brand)]" /> Richiami</h1>
        <select data-testid="richiami-salon-filter" className="sp-input !w-auto !py-1.5 text-sm ml-auto" value={salon} onChange={(e) => setSalon(e.target.value)}>
          <option value="all">Tutti i miei saloni</option>
          {availableTenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button onClick={load} className="sp-btn-ghost inline-flex items-center gap-1 text-sm"><RefreshCw className="h-4 w-4" /></button>
      </div>

      {loading ? (
        <div className="text-center text-sm text-[color:var(--sp-text-muted)] py-10">Carico…</div>
      ) : (
        <>
        <div className="flex flex-wrap gap-2 mb-4 text-[10px]" data-testid="cb-legend">
          {CB_LEGEND.map((l) => <span key={l.label} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-bold ${l.cls}`}>{l.label}</span>)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Da fare oggi" items={groups.today} onOpen={setDetail} tid="richiami-today" accent="text-[color:var(--sp-brand)]" />
          <Section title="Scaduti" items={groups.overdue} onOpen={setDetail} tid="richiami-overdue" accent="text-rose-600" />
          <Section title="Prossimi" items={groups.upcoming} onOpen={setDetail} tid="richiami-upcoming" />
          <Section title="Completati" items={groups.completed} onOpen={setDetail} tid="richiami-completed" accent="text-emerald-600" />
        </div>
        </>
      )}

      {detail && (
        <CallbackDetail callback={detail} tenantClient={createAgencyClient(() => detail.tenant?.id)} onClose={() => setDetail(null)} onChanged={load}
          onOpenContact={(cb) => { setDetail(null); setContact(cb); }}
          onScheduleFollowUp={(cb) => setFollowUp(cb)} />
      )}
      {contact && (
        <AgencyContactDrawerHost clientId={contact.client_id} tenant={contact.tenant}
          caseObj={contact.case ? { id: contact.agency_case_id, ...contact.case } : null}
          onClose={() => setContact(null)} onUpdated={load} />
      )}
      {followUp && (
        <CallbackModal tenant={followUp.tenant} clientId={followUp.client_id} clientName={followUp.client?.name}
          caseObj={followUp.agency_case_id ? { id: followUp.agency_case_id } : null}
          onClose={() => setFollowUp(null)} onSaved={load} />
      )}
    </div>
  );
}
